import React, { useEffect, useRef, useState } from "react";
import { FaWhatsapp, FaMapMarkerAlt, FaClock, FaPhone, FaEnvelope, FaInstagram, FaFacebookF, FaArrowDown, FaStar, FaCheck } from "react-icons/fa";
import logo from "../assets/logo.png";
import MyNavbar from "../components/MyNavbar";
import Seo from "../seo/Seo";
import { seoConfig } from "../seo/seoConfig";
import "../css/Estetica.css";

// Particle component for background effect
const ParticleField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationFrameId;
    let particles = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Create particles
    const createParticles = () => {
      particles = [];
      const particleCount = Math.floor((canvas.width * canvas.height) / 15000);
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 2 + 0.5,
          speedX: (Math.random() - 0.5) * 0.5,
          speedY: (Math.random() - 0.5) * 0.5,
          opacity: Math.random() * 0.5 + 0.2,
        });
      }
    };

    createParticles();

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle, index) => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(107, 123, 180, ${particle.opacity})`;
        ctx.fill();

        // Draw connections
        particles.forEach((otherParticle, otherIndex) => {
          if (index !== otherIndex) {
            const dx = particle.x - otherParticle.x;
            const dy = particle.y - otherParticle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 120) {
              ctx.beginPath();
              ctx.strokeStyle = `rgba(107, 123, 180, ${0.1 * (1 - distance / 120)})`;
              ctx.lineWidth = 0.5;
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(otherParticle.x, otherParticle.y);
              ctx.stroke();
            }
          }
        });
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas ref={canvasRef} className="particle-canvas" />;
};

// Floating WhatsApp CTA
const FloatingWhatsApp = () => {
  const phoneNumber = "+351962281179";
  const message =
    "Olá! Gostaria de saber mais sobre os serviços de estética do LifeCenter.";
  const encodedMessage = encodeURIComponent(message);
  const whatsappURL = `https://wa.me/${phoneNumber.replace(/\s/g, "")}?text=${encodedMessage}`;

  return (
    <a
      href={whatsappURL}
      target="_blank"
      rel="noopener noreferrer"
      className="floating-whatsapp"
    >
      <div className="whatsapp-pulse"></div>
      <FaWhatsapp className="whatsapp-icon-large" />
      <span className="whatsapp-text">Fale Connosco</span>
    </a>
  );
};

// Service Card Component
const ServiceCard = ({ image: Image, title, description, delay }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 },
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={cardRef} className={`service-card ${isVisible ? "visible" : ""}`}>
      <div className="service-icon-wrapper">
        <div className="service-icon-bg"></div>
        <img src={Image} alt={title} className="service-icon" />
      </div>
      <h3>{title}</h3>
      <p>{description}</p>
      <a href="#contacto" className="service-cta">
        Saber Mais <FaArrowDown className="rotate-arrow" />
      </a>
    </div>
  );
};

// Testimonial Component with AI-generated faces placeholder
const TestimonialCard = ({ name, text, rating, imageIndex }) => {
  return (
    <div className="testimonial-card">
      <div className="testimonial-image">
        <div className="ai-face-placeholder" data-index={imageIndex}>
          <span>{name.charAt(0)}</span>
        </div>
      </div>
      <div className="testimonial-stars">
        {[...Array(rating)].map((_, i) => (
          <FaStar key={i} className="star-icon" />
        ))}
      </div>
      <p className="testimonial-text">"{text}"</p>
      <p className="testimonial-name">{name}</p>
    </div>
  );
};

const LandingPage = () => {
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    servico: "",
    mensagem: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);

      // Send to WhatsApp as backup
      const phoneNumber = "+351962281179";
      const message = `Novo contacto do site!\n\nNome: ${formData.nome}\nEmail: ${formData.email}\nTelefone: ${formData.telefone}\nServiço: ${formData.servico}\nMensagem: ${formData.mensagem}`;
      const encodedMessage = encodeURIComponent(message);
      window.open(
        `https://wa.me/${phoneNumber}?text=${encodedMessage}`,
        "_blank",
      );

      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setFormData({
          nome: "",
          email: "",
          telefone: "",
          servico: "",
          mensagem: "",
        });
      }, 3000);
    }, 1500);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="landing-page">
      <Seo {...seoConfig.estetica} />
      <MyNavbar />
      <ParticleField />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-logo-container">
            <img src={logo} alt="Life Center" className="hero-logo" />
          </div>
          <h1 className="hero-title">
            <span className="title-line">Bem-vindo ao</span>
            <span className="title-highlight">Life Center</span>
          </h1>
          <p className="hero-subtitle">
            O seu centro de excelência em <strong>Estética</strong> e{" "}
            <strong>Medicina Estética</strong>. Descubra tratamentos
            personalizados que realçam a sua beleza natural e promovem o seu
            bem-estar.
          </p>
          <div className="hero-cta-group">
            <a
              href={`https://wa.me/+351962281179?text=${encodeURIComponent("Olá! Gostaria de agendar uma consulta de estética.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button primary"
            >
              <FaWhatsapp /> Agendar Consulta
            </a>
            <button
              onClick={() => scrollToSection("servicos")}
              className="cta-button secondary"
            >
              Conhecer Serviços
            </button>
          </div>
        </div>
        <div
          className="hero-scroll-indicator"
          onClick={() => scrollToSection("servicos")}
        >
          <span>Descubra Mais</span>
          <FaArrowDown className="bounce-arrow" />
        </div>
        <div className="hero-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section" id="sobre">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Sobre Nós</span>
            <h2>O Que é o Life Center?</h2>
          </div>
          <div className="about-content">
            <div className="about-text">
              <p className="lead-text">
                O <strong>Life Center</strong> é um espaço dedicado à sua saúde,
                beleza e bem-estar, localizado no coração de Portugal.
              </p>
              <p>
                Combinamos as mais avançadas técnicas de{" "}
                <strong>estética e medicina estética</strong> com um atendimento
                personalizado e acolhedor. A nossa equipa de profissionais
                qualificados trabalha para realçar a sua beleza natural,
                respeitando sempre a sua individualidade.
              </p>
              <div className="about-features">
                <div className="feature-item">
                  <FaCheck className="feature-check" />
                  <span>Profissionais Certificados</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="feature-check" />
                  <span>Tecnologia de Ponta</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="feature-check" />
                  <span>Tratamentos Personalizados</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="feature-check" />
                  <span>Ambiente Acolhedor</span>
                </div>
              </div>
              <a
                href={`https://wa.me/+351962281179?text=${encodeURIComponent("Olá! Quero saber mais sobre o Life Center.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button primary inline-cta"
              >
                <FaWhatsapp /> Fale Connosco
              </a>
            </div>
            <div className="about-visual">
              <div className="visual-card">
                <div className="visual-number">10+</div>
                <div className="visual-label">Anos de Experiência</div>
              </div>
              <div className="visual-card">
                <div className="visual-number">5000+</div>
                <div className="visual-label">Clientes Satisfeitos</div>
              </div>
              <div className="visual-card">
                <div className="visual-number">20+</div>
                <div className="visual-label">Tratamentos</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section" id="servicos">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Nossos Serviços</span>
            <h2>Estética & Medicina Estética</h2>
            <p className="section-description">
              Descubra os nossos tratamentos exclusivos, pensados para realçar a
              sua beleza e promover o seu bem-estar.
            </p>
          </div>
          <div className="services-grid">
            <ServiceCard
              image="/Facial Aesthetics.png"
              title="Medicina Estética Facial"
              description="Tratamentos avançados como toxina botulínica, preenchimentos com ácido hialurónico, bioestimuladores de colagénio e harmonização facial."
              delay={0}
            />
            <ServiceCard
              image="/Facial Treatments.png"
              title="Tratamentos de Rosto"
              description="Revitalização facial, limpeza de pele profunda, tratamentos para manchas, rugas, flacidez e protocolos anti-aging personalizados."
              delay={100}
            />
            <ServiceCard
              image="/Body Treatments.png"
              title="Tratamentos Corporais"
              description="Drenagem linfática, massagens terapêuticas e de relaxamento, tratamentos para estrias, celulite, gordura localizada e flacidez."
              delay={200}
            />
            <ServiceCard
              image="/Advanced Aesthetics.png"
              title="Estética Avançada"
              description="Peelings químicos, microagulhamento, HIFU, radiofrequência, fototerapia e tecnologias avançadas para rejuvenescimento."
              delay={300}
            />
          </div>
          <div className="services-cta">
            <a
              href={`https://wa.me/+351962281179?text=${encodeURIComponent("Olá! Gostaria de mais informações sobre os tratamentos de estética.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button primary large"
            >
              <FaWhatsapp /> Agende a Sua Avaliação
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section" id="testemunhos">
        <div className="container">
          <div className="section-header light">
            <span className="section-tag">Testemunhos</span>
            <h2>O Que Dizem os Nossos Clientes</h2>
          </div>
          <div className="testimonials-grid">
            <TestimonialCard
              name="Bernardo Lopes"
              text="Resultados incríveis! A equipa é muito profissional e atenciosa. Recomendo a 100%!"
              rating={5}
              imageIndex={1}
            />
            <TestimonialCard
              name="Ana Costa"
              text="Finalmente encontrei um lugar onde me sinto cuidada. Os tratamentos são de excelência."
              rating={5}
              imageIndex={2}
            />
            <TestimonialCard
              name="João Veloso"
              text="Adorei a experiência! Ambiente super acolhedor e resultados visíveis desde a primeira sessão."
              rating={5}
              imageIndex={3}
            />
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-section" id="contacto">
        <div className="container">
          <div className="contact-wrapper">
            <div className="contact-info">
              <div className="section-header left">
                <span className="section-tag">Contacto</span>
                <h2>Fale Connosco</h2>
              </div>
              <p className="contact-intro">
                Tem dúvidas ou quer agendar uma consulta? Preencha o formulário
                ou contacte-nos diretamente.
              </p>
              <div className="contact-methods">
                <a
                  href={`https://wa.me/+351962281179`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-method whatsapp-method"
                >
                  <FaWhatsapp className="method-icon" />
                  <div className="method-info">
                    <span className="method-label">WhatsApp</span>
                    <span className="method-value">+351 962 281 179</span>
                  </div>
                </a>
                <div className="contact-method">
                  <FaPhone className="method-icon" />
                  <div className="method-info">
                    <span className="method-label">Telefone</span>
                    <span className="method-value">+351 962 281 179</span>
                  </div>
                </div>
                <div className="contact-method">
                  <FaEnvelope className="method-icon" />
                  <div className="method-info">
                    <span className="method-label">Email</span>
                    <span className="method-value">info@o2-lifecenter.com</span>
                  </div>
                </div>
              </div>
              <div className="social-links">
                <a
                  href="https://www.instagram.com/o2lifecenter/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  <FaInstagram />
                </a>
                <a
                  href="https://www.facebook.com/lifecenterlisboa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  <FaFacebookF />
                </a>
              </div>
            </div>
            <div className="contact-form-container">
              <form
                onSubmit={handleSubmit}
                className={`contact-form ${submitSuccess ? "success" : ""}`}
              >
                {submitSuccess ? (
                  <div className="success-message">
                    <div className="success-icon">✓</div>
                    <h3>Mensagem Enviada!</h3>
                    <p>Entraremos em contacto brevemente.</p>
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleInputChange}
                        placeholder="O seu nome *"
                        required
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Email *"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <input
                          type="tel"
                          name="telefone"
                          value={formData.telefone}
                          onChange={handleInputChange}
                          placeholder="Telefone *"
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <select
                        name="servico"
                        value={formData.servico}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Selecione um serviço *</option>
                        <option value="medicina-estetica">
                          Medicina Estética Facial
                        </option>
                        <option value="tratamentos-rosto">
                          Tratamentos de Rosto
                        </option>
                        <option value="tratamentos-corpo">
                          Tratamentos Corporais
                        </option>
                        <option value="estetica-avancada">
                          Estética Avançada
                        </option>
                        <option value="outro">Outro / Consulta Geral</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <textarea
                        name="mensagem"
                        value={formData.mensagem}
                        onChange={handleInputChange}
                        placeholder="A sua mensagem..."
                        rows="4"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="submit-button"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="loading-spinner"></span>
                      ) : (
                        <>
                          <FaWhatsapp /> Enviar Mensagem
                        </>
                      )}
                    </button>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src={logo} alt="Life Center" className="footer-logo" />
              <p>O seu centro de excelência em estética e bem-estar.</p>
            </div>
            <div className="footer-info">
              <div className="footer-block">
                <h4>
                  <FaMapMarkerAlt /> Localização
                </h4>
                <p>
                  Avenida de Pádua nº 10 L
                  <br />
                  1800-297 Lisboa
                  <br />
                  Portugal
                </p>
                <a
                  href="https://www.google.com/maps/place/Life+Center/@38.7630983,-9.1054915,17z"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="map-link"
                >
                  Ver no Google Maps →
                </a>
              </div>
              <div className="footer-block">
                <h4>
                  <FaClock /> Horário
                </h4>
                <p>
                  Segunda a Sexta: 7h - 21h
                  <br />
                  Sábado: 9h - 14h
                  <br />
                  Domingo: Fechado
                </p>
              </div>
            </div>
            <div className="footer-cta">
              <h4>Pronto para Começar?</h4>
              <a
                href={`https://wa.me/+351962281179?text=${encodeURIComponent("Olá! Quero agendar uma consulta.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button primary"
              >
                <FaWhatsapp /> Agendar Agora
              </a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>
              © {new Date().getFullYear()} Life Center. Todos os direitos
              reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <FloatingWhatsApp />
    </div>
  );
};

export default LandingPage;
